import twoConcernsInClass from './2-concerns-in-class.png'
import concernsCoupling from './concerns-coupling.png'
import couplingWithOtherComponents from './coupling-with-other-components.png'
import cohesiveClasses from './cohesive-classes.png'
import conversation from './conversation.png'
import conversationSupportChat from './conversation-support-chat.png'
import couplingTitleSupportTopic from './coupling-title-support-topic.png'
import separateSupportConversation from './separate-support-conversation.png'
import couplingReportMessages from './coupling-report-messages.png'
import reportDecoupling from './report-decoupling.png'

const content = `<p>The design principle of High cohesion consists in having units of code focused around one thing only. This applies at all scales in the code: functions, classes and larger components.</p>

<h2 id="The-concepts-of-cohesion">The concepts of cohesion</h2>

<p>In a piece of code with high cohesion, everything contributes towards achieving the same goal. Conversely, low cohesion is when a piece of code contains several concerns:</p>

<img src=${twoConcernsInClass} alt="2-concerns-in-class" />

<p>The problem with low cohesion is that it creates coupling, of two sorts.</p>

<p>The first type of coupling is an <strong>internal coupling</strong> between the two concerns. Since they’re in the same class for example, their is no boundary between them and they can access everything from one another:</p>

<img src=${concernsCoupling} alt="concerns-coupling" />

<p>This coupling between the two concerns makes them harder to understand and to change than if they were separate. We’ll see how that looks like on an example.</p>

<p>Another type of coupling is the <strong>external coupling</strong> with other components. Two concerns have more reasons to interact with other components than one concern, so the class knows about more collaborators:</p>

<img src=${couplingWithOtherComponents} alt="coupling-with-other-components" />

<p>Better cohesion means having components focused on one concern. This leads to lower coupling:</p>

<img src=${cohesiveClasses} alt="cohesive-classes" />

<p>These are the theoretical aspects of high cohesion. Let’s take an example to see what that looks like in practice.</p>

<h2 id="Example">Example</h2>

<p>Let’s take the example of an internal chat application. Users can exchange messages in conversations with each other, and the title of the conversation is “Conversation between” followed by the list of the members of the conversation. The conversation is represented by the class <code>Conversation</code>:</p>

<img src=${conversation} alt="conversation" />

<p>A new requirement comes in: we want to have special conversations to represent requests to the technical support. They’re special because they have a special screen to create them, where the user selects the topic they need support with (access rights to an application, issue with the printer, etc.), and a description of the problem.</p>

<p>After the user inputs this information, the system generates the first message of the conversation containing the description of the issue that the user input.</p>

<p>The title of support request conversations should be “Support request on ” followed by the support topic.</p>

<p>One way to represent this is to make the <code>Conversation</code> class handle support requests. This design option has low cohesion because it mixes in the same class the concerns related to chatting and the concerns related to technical support:</p>

<img src=${conversationSupportChat} alt="conversation-support-chat" />

<p>Let’s see how this low cohesion leads to high coupling.</p>

<h3 id="Internal-coupling">Internal coupling</h3>

<p>The first type of coupling we described was internal coupling. One example that can appear in the code is to determine the title:</p>

<pre><code class="language-ruby">if support_topic.nil?
  title = "Conversations between #{members&#91;0].name} and #{members&#91;1].name}"
else
  title = "Support request on #{support_topic}"
end</code></pre>

<p>This means that the chat concern of title knows about the support concern of support topic:</p>

<img src=${couplingTitleSupportTopic} alt="coupling-title-support-topic" />

<p>This means that if we expected a different format of title for the support request or if we change the way that the support topic is represented (from a string to a class for example), the code handling chat concerns of the conversation also has to change.</p>

<p>When this happens several times in the class, the coupling makes it hard to change the conversation and the support request with making heavier changes that impacts the two of them.</p>

<h3 id="How-to-fix">How to fix</h3>

<p>To improve the design we can increase the cohesion of the code by having separate classes to represent the conversation and the support request:</p>

<img src=${separateSupportConversation} alt="separate-support-conversation" />

<p>The conversation can have an optional parameter for its title. If it’s not passed, the conversation computes it based on its members:</p>

<pre><code class="language-ruby">class Conversation
  def initialize(members, title: nil)
    @title = title || "Conversations between #{members&#91;0].name} and #{members&#91;1].name}"
  end
  
end</code></pre>

<p>The support request that passes the title that suits it:</p>

<pre><code class="language-ruby">class SupportRequest
  def initialize(members, topic)
    @conversation = Conversation.new(members, "Support request on #{topic}")
  end
  
end</code></pre>

<p>This way, if the format of the title for the support request or the representation of the support topic changes, it’s the code of the support that needs to change, and not the code of the conversation.</p>

<h3 id="External-coupling">External coupling</h3>

<p>If we go back to the design with low cohesion where the conversation class handles both the support request and the chat, there is another form of coupling that comes with the interaction of other components.</p>

<p>Here is a requirement that comes with the support requests: at the end of the year, the IT department wants to generate a report that lists the descriptions of all the support requests that were created during the year.</p>

<p>Let’s examine the external coupling in this design. The description of the request is the first message of the conversation, so to get the description of the request the report has to get the first message of the conversation. Here is a possible implementation in Rails:</p>

<pre><code class="language-ruby">class Report
  def initialize(since)
    @since = since
  end
  
  def generate
    Conversation
      .where.not(support_topic: nil)
      .where('conversations.created_at > ', @since)
      .joins(:messages)
      .where('messages.nth', 0)
      .select('messages.content')
  end
end</code></pre>

<p>The reports know about messages, and in particular that the first message of the conversation is the description of the request. It also knows that the conversations that represent the support requests are those that have a support topic.</p>

<img src=${couplingReportMessages} alt="coupling-report-messages" />

<p>This creates coupling between the report and the conversation. If any of the following comes to change, this requires to change the code of the report:</p>

<ul><li>if we want another message (such as “How can we help you today?”) before the description</li><li>if we implement a policy of data retention where messages are deleted after 6 months</li><li>if we allow support requests without a support topic</li></ul>

<p>Such changes are unrelated to the report, but the coupling with the conversation make the code of the report need to adapt to them.</p>

<h3 id="How-to-fix.1">How to fix</h3>

<p>Splitting the class into separate concerns helps reduce external coupling too. The support request can have its own description, and the conversation can expose a method <code>send_message</code> to add messages to it:</p>

<pre><code class="language-ruby">class SupportRequest
  def initialize(members, topic, description)
    @conversation = Conversation.new(members, "Support request on #{topic}")
    @conversation.send_message(from: members&#91;0], content: description)
    @description = description
  end
end</code></pre>

<p>The report can only access the support request:</p>

<pre><code class="language-ruby">class Report
  def initialize(since)
    @since = since
  end
  
  def generate
    descriptions = SupportRequest
      .where('created_at > ', @since)
      .pluck(:description)
  end
end</code></pre>

<p>The report is no longer coupled to the messages and to the support topic:</p>

<img src=${reportDecoupling} alt="report-decoupling" />

<p>If the following kinds of changes happen, the report code is not impacted:</p>

<ul><li>if we want another message before the description, we can add it before the message of the description</li><li>if we implement a policy of data retention where messages are deleted after 6 months, it doesn’t impact the report because its description keeps being stored in the support request</li><li>if we allow support requests without a support topic, it’s ok because the report no longer uses it</li></ul>

<h2 id="Low-cohesion-compounds">Low cohesion compounds</h2>

<p>All those examples mixed two concerns: conversation and support requests. But in practice nothing prevent 3 or more concepts to get together in a class. And when classes start losing cohesion, they become more and more difficult to fix when new concepts keep being added. The more concerns added, the more coupling between them and with the rest of the code.</p>

<p>It’s important to be alert for functions, classes or larger components that lose cohesion. One simple hint to detect them is to pay attention to naming. If something is difficult to name or has several concepts in its name, it may be because it doesn’t represent one thing. If it is the case, it’s beneficial to split it before adding more code to it.</p>

<div class="notes">
<p>Key points:</p>

<ul><li>High cohesion is when all the code contributes to one responsibility</li><li>Cohesion applies to functions, classes and larger components</li><li>Low cohesion opens the door to internal and external coupling</li><li>Split code to have smaller components focused on one concern</li><li>Be on the lookout for weird names or difficult names that could indicate low cohesion</li></ul>
</div>`

export default content
